import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import InstagramFeed from './InstagramFeed';
import './HomePage.css';

const {
  FiArrowRight,
  FiEye,
  FiFlag,
  FiHeart,
  FiShield,
  FiUsers,
  FiEdit3
} = FiIcons;

const profilePhoto = 'https://media-manager-c.questera.ai/greta-media/84bd1a1ec4b169d3b45fb6d9222544ea571861834b2a41df59b8dd4fbd9877604e1a1a824c0c77323e9e6a3b81021929/images/aW1hZ2UvanBlZw==/885093e451df96d7d2b4f9efff86b64a.jpg';

const reportingSteps = [
  {
    number: '01',
    icon: FiEdit3,
    title: 'Share the case',
    description: 'Submit a public post link and a short, factual description. Leave out private or identifying details.',
    action: 'Submit a case',
    page: 'Submit a Case'
  },
  {
    number: '02',
    icon: FiFlag,
    title: 'Our community reports it',
    description: 'The case is shared with the community. Members can report the original post through the platform and confirm when they have done so.',
    action: 'Browse community cases',
    page: 'Community Feed'
  },
  {
    number: '03',
    icon: FiShield,
    title: 'Follow through safely',
    description: 'The platform reviews reports according to its policies. Avoid engaging or retaliating, and reach out to someone you trust if support is needed.',
    action: 'Find support',
    page: 'Support'
  }
];

function HowItWorks({ onNavigate }) {
  return (
    <motion.section
      className="how-it-works"
      aria-labelledby="how-it-works-title"
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4 }}
    >
      <div className="how-it-works-heading">
        <span className="eyebrow">A simple, safer approach</span>
        <h2 id="how-it-works-title">How it works</h2>
        <p>Take one thoughtful step at a time. Reporting is always your choice.</p>
      </div>
      <div className="how-it-works-grid">
        {reportingSteps.map((step) => (
          <article className="how-step-card" key={step.number}>
            <div className="how-step-top">
              <span className="how-step-icon">
                <SafeIcon icon={step.icon} />
              </span>
              <span className="how-step-number">STEP {step.number}</span>
            </div>
            <h3>{step.title}</h3>
            <p>{step.description}</p>
            <button
              className="how-step-link"
              type="button"
              onClick={() => onNavigate(step.page)}
            >
              {step.action}
              <SafeIcon icon={FiArrowRight} />
            </button>
          </article>
        ))}
      </div>
    </motion.section>
  );
}

function HomePage({ cases, onNavigate, totalViews = 0 }) {
  return (
    <div className="home-page">
      <motion.section
        className="hero-card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="hero-copy">
          <span className="eyebrow">
            <SafeIcon icon={FiShield} /> A safer internet starts together
          </span>
          <h1>
            Together, we stand against online bullying.{' '}
            <em>Share. Report. Protect.</em>
          </h1>
          <p>
            A calm, supportive community helping people take thoughtful action
            when harmful posts appear online.
          </p>
          <div className="hero-actions">
            <button
              className="button button-primary"
              onClick={() => onNavigate('Submit a Case')}
            >
              Submit a Case <SafeIcon icon={FiArrowRight} />
            </button>
            <button
              className="button button-secondary"
              onClick={() => onNavigate('Community Feed')}
            >
              Browse community cases
            </button>
          </div>
          <div className="hero-trust">
            <span><SafeIcon icon={FiHeart} /> Care comes first</span>
            <span><SafeIcon icon={FiUsers} /> No retaliation</span>
            <span><SafeIcon icon={FiEye} /> {totalViews.toLocaleString()} total views</span>
          </div>
        </div>
        <div className="hero-art" style={{ minHeight: 410 }}>
          <div className="orbit orbit-one" />
          <div className="orbit orbit-two" />
          <div className="doctor-badge" style={{ width: 390, height: 390 }}>
            <img
              className="doctor-icon"
              src={profilePhoto}
              alt="Prof. Dr. Vik"
              style={{
                width: 360,
                height: 360,
                borderRadius: '50%',
                objectFit: 'cover',
                marginBottom: 0
              }}
            />
          </div>
          <div className="floating-note note-one">You are not alone <span>♡</span></div>
          <div className="floating-note note-two">
            <SafeIcon icon={FiHeart} /> Small actions matter
          </div>
        </div>
      </motion.section>

      <HowItWorks onNavigate={onNavigate} />

      <section className="quick-stats">
        <div>
          <span className="stat-icon teal"><SafeIcon icon={FiShield} /></span>
          <b>Support, not retaliation</b>
          <small>Report through the platform</small>
        </div>
        <div>
          <span className="stat-icon blue"><SafeIcon icon={FiUsers} /></span>
          <b>Open community feed</b>
          <small>Cases are visible after submission</small>
        </div>
        <div>
          <span className="stat-icon peach"><SafeIcon icon={FiHeart} /></span>
          <b>Open to everyone</b>
          <small>No account needed to submit a case</small>
        </div>
      </section>

      <section className="content-grid">
        <div className="section-card latest-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Shared with the community</span>
              <h2>Latest cases</h2>
            </div>
            <button className="text-button" onClick={() => onNavigate('Community Feed')}>
              See all <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
          {cases.slice(0, 3).map((item) => (
            <div className="latest-case" key={item.id}>
              <div className="platform-avatar">{item.platform.slice(0, 1)}</div>
              <div>
                <b>{item.category}</b>
                <p>{item.description}</p>
                <small>
                  {item.platform} · {item.date} · {Number(item.views || 0)} views · {item.reports || 0} reports
                </small>
              </div>
            </div>
          ))}
          {cases.length === 0 && (
            <p className="empty-copy">No community cases to show yet.</p>
          )}
        </div>
      </section>

      <div className="care-note">
        <SafeIcon icon={FiHeart} />
        <span>
          <b>Need immediate help?</b> If you are in danger or feeling unsafe,
          contact local emergency services or a trusted person.
        </span>
        <button onClick={() => onNavigate('Support')}>Safety resources</button>
      </div>
      <InstagramFeed />
    </div>
  );
}

export default HomePage;